import type { ReactNode } from 'react';

type UploadRowError = {
  row?: number;
  field?: string;
  message?: string;
  error?: string;
};

const formatLabel = (value: string) =>
  value
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/^./, char => char.toUpperCase());

const getBackendPayload = (error: any) =>
  error?.response?.data || error?.data || error || {};

const getFriendlyDatabaseMessage = (error: any) => {
  const name = error?.name || error?.original?.name || '';
  const message = String(error?.message || error?.original?.message || '');

  if (name === 'SequelizeUniqueConstraintError') {
    return 'This record already exists in the system.';
  }

  if (name === 'SequelizeValidationError') {
    return 'Some uploaded data is invalid. Please review the highlighted rows.';
  }

  if (name === 'SequelizeForeignKeyConstraintError') {
    return 'Related data was not found. Please verify IDs and linked records in the file.';
  }

  if (/foreign key constraint/i.test(message)) {
    return 'Related data was not found. Please verify IDs and linked records in the file.';
  }

  if (/duplicate|unique/i.test(message)) {
    return 'This record already exists in the system.';
  }

  if (/validation/i.test(message)) {
    return 'Some uploaded data is invalid. Please review the file and upload again.';
  }

  return '';
};

const normalizeRowErrors = (rows: UploadRowError[] = []) =>
  rows
    .map(row => ({
      row: row.row,
      field: row.field,
      message: row.message || row.error,
    }))
    .filter(row => row.message);

const renderRowErrors = (rows: ReturnType<typeof normalizeRowErrors>) => {
  if (!rows.length) return null;

  const visibleRows = rows.slice(0, 5);
  const hiddenCount = rows.length - visibleRows.length;

  return (
    <div className="mt-2 small">
      <div className="fw-semibold">Rows to fix:</div>
      {visibleRows.map((row, index) => (
        <div key={`${row.row || 'row'}-${row.field || index}`}>
          {row.row ? `Row ${row.row}: ` : ''}
          {row.field ? `${formatLabel(row.field)} - ` : ''}
          {row.message}
        </div>
      ))}
      {hiddenCount > 0 && <div>And {hiddenCount} more row error(s).</div>}
    </div>
  );
};

export const getUploadErrorToastContent = (error: any): ReactNode => {
  const payload = getBackendPayload(error);
  const rowErrors = normalizeRowErrors(payload.errors || payload.failedRows);
  const databaseMessage = getFriendlyDatabaseMessage(payload);
  const message =
    databaseMessage ||
    payload.message ||
    payload.error ||
    'Upload failed. Please review the file and try again.';

  return (
    <div>
      <div className="fw-semibold">{message}</div>
      {renderRowErrors(rowErrors)}
      {payload.hint && <div className="mt-2 small">{payload.hint}</div>}
    </div>
  );
};

export const getUploadSuccessToastContent = (data: any): ReactNode => {
  const failedRows = normalizeRowErrors(data?.failedRows);

  if (failedRows.length) {
    return (
      <div>
        <div className="fw-semibold">
          Some rows failed to process. Please review and upload again.
        </div>
        <div className="small mt-1">
          Inserted: {data?.inserted || 0}, Updated: {data?.updated || 0},
          Failed: {data?.failed || failedRows.length}
        </div>
        {renderRowErrors(failedRows)}
        {data?.hint && <div className="mt-2 small">{data.hint}</div>}
      </div>
    );
  }

  if (typeof data?.inserted === 'number' || typeof data?.updated === 'number') {
    return `File uploaded successfully. Inserted: ${data?.inserted || 0}, Updated: ${data?.updated || 0}.`;
  }

  return data?.message || 'File uploaded successfully';
};

export const getUploadToastType = (data: any) =>
  data?.failedRows?.length || data?.failed > 0 ? 'warning' : 'success';
